// Silueta corporal del 003 (bloque H): 4 vistas esquemáticas cuyas regiones se marcan con un clic para señalar la región o
// sistema afectado, como pide el instructivo ("se pintará la región... en el gráfico"). La geometría es la misma que dibuja el
// PDF firmado (`@logiacare/hce-pdf` → `siluetaCorporal.ts`). Equivalencia funcional, no facsímil.
import { ALTO_VISTA_SILUETA, ANCHO_VISTA_SILUETA, claveRegionSilueta, resumenSilueta, VISTAS_SILUETA, type FormaSilueta } from '@logiacare/hce-pdf';
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

export interface SiluetaCorporalProps {
  /** Claves `<vista>:<región>` marcadas. */
  readonly regiones: readonly string[];
  readonly onChange: (regiones: string[]) => void;
}

function Forma({ forma, activa, etiqueta, onClick }: { readonly forma: FormaSilueta; readonly activa: boolean; readonly etiqueta: string; readonly onClick: () => void }): JSX.Element {
  const clases = cn('cursor-pointer stroke-[1] transition-colors focus:outline-none focus-visible:stroke-2', activa ? 'fill-red-500/70 stroke-red-700' : 'fill-muted stroke-border hover:fill-red-200');
  const comunes = { className: clases, role: 'button', tabIndex: 0, 'aria-pressed': activa, 'aria-label': etiqueta, onClick, onKeyDown: (e: React.KeyboardEvent) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), onClick()) } as const;
  return forma.tipo === 'rect' ? <rect x={forma.x} y={forma.y} width={forma.w} height={forma.h} rx={4} {...comunes} /> : <ellipse cx={forma.cx} cy={forma.cy} rx={forma.rx} ry={forma.ry} {...comunes} />;
}

export function SiluetaCorporal({ regiones, onChange }: SiluetaCorporalProps): JSX.Element {
  const { t } = useTranslation();
  const marcadas = new Set(regiones);
  const alternar = (clave: string): void => onChange(marcadas.has(clave) ? regiones.filter((r) => r !== clave) : [...regiones, clave]);
  const resumen = resumenSilueta(regiones);

  return (
    <div className="flex flex-col gap-2">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {VISTAS_SILUETA.map((v) => (
          <figure key={v.vista} className="flex flex-col items-center gap-1 rounded-md border border-border p-2">
            <svg viewBox={`0 0 ${ANCHO_VISTA_SILUETA} ${ALTO_VISTA_SILUETA}`} className="h-56 w-auto" aria-label={v.etiqueta}>
              {v.regiones.map((r) => {
                const clave = claveRegionSilueta(v.vista, r.clave);
                return <Forma key={clave} forma={r.forma} activa={marcadas.has(clave)} etiqueta={`${v.etiqueta}: ${r.etiqueta}`} onClick={() => alternar(clave)} />;
              })}
            </svg>
            <figcaption className="text-xs text-hce-muted">{v.etiqueta}</figcaption>
          </figure>
        ))}
      </div>
      <p className="text-xs text-hce-muted">{resumen ? `${t('formularios.f003.siluetaMarcadas')}: ${resumen}` : t('formularios.f003.siluetaAyuda')}</p>
    </div>
  );
}
